import { computed, Ref } from 'vue';
import { IYearMonth } from '@/coreDomainsModule/date';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { IPreviewHeader, ISelectableItem, OutputFormat, PreviewHeaderType } from '../domains/entities/InventoryTemplate';
import { MAX_PREVIEW_HEADERS, isSingleFieldOutputKey } from '../constants/inventoryTemplateConstants';

// 過去月のみ表示する項目（実績データ）
const PAST_ONLY_OUTPUT_KEYS = ['salesPerformance', 'arrivalPerformance'];

/**
 * 指定された項目が過去月のみ表示対象かどうかを判定
 * @param key - 出力データ項目のキー
 * @returns 過去月のみ表示対象の場合true
 */
const isPastOnlyOutputKey = (key: string): boolean => {
  return PAST_ONLY_OUTPUT_KEYS.includes(key);
};

/**
 * 指定された年月が当月以降（未来月）かどうかを判定
 * 月末在庫と同じロジック: month_offset >= 0 の場合は未来扱い
 * @param yearMonth - 判定対象の年月
 * @returns 当月以降の場合true
 */
const isFutureMonth = (yearMonth: IYearMonth): boolean => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  // 月の相対位置を計算（負: 過去, 0: 当月, 正: 未来）
  const monthOffset = (yearMonth.year - currentYear) * 12 + (yearMonth.month - currentMonth);
  return monthOffset >= 0;
};

/**
 * useTemplatePreviewHeadersの戻り値の型
 */
export interface UseTemplatePreviewHeadersReturn {
  /** プレビューヘッダーリスト */
  previewHeaders: Ref<IPreviewHeader[]>;
  /** プレビューの表示判定 */
  showPreview: Ref<boolean>;
  /** 表示しきれない列数 */
  otherColumnsCount: Ref<number>;
}

/**
 * テンプレート機能用のプレビューヘッダーを生成するコンポーザブル
 * @param selectedMasterItems - 選択されたマスタ項目
 * @param selectedOutputDataItems - 選択された出力データ項目
 * @param startYearMonth - 開始年月
 * @param endYearMonth - 終了年月
 * @param outputFormat - 出力形式
 * @returns プレビューヘッダー管理用の関数群
 */
export const useTemplatePreviewHeaders = (
  selectedMasterItems: Ref<ISelectableItem[]>,
  selectedOutputDataItems: Ref<ISelectableItem[]>,
  startYearMonth: Ref<IYearMonth>,
  endYearMonth: Ref<IYearMonth>,
  outputFormat: Ref<OutputFormat>
): UseTemplatePreviewHeadersReturn => {
  /**
   * プレビューヘッダーを生成
   */
  const previewHeaders = computed<IPreviewHeader[]>(() => {
    const headers: IPreviewHeader[] = [];
    let index = 0;

    // 出力設定が設定されているかチェック
    if (!startYearMonth.value || !endYearMonth.value || !outputFormat.value) {
      return headers;
    }

    // 選択されたマスタ項目を追加
    selectedMasterItems.value.forEach((item) => {
      headers.push({
        id: `master-${item.key}-${index}`,
        label: item.label,
        type: PreviewHeaderType.MASTER,
        masterKey: item.key,
        originalIndex: index++,
      });
    });

    if (selectedOutputDataItems.value.length === 0) {
      return headers;
    }

    // 月範囲を取得
    const yearMonths = DateService.getYearMonthsBetween(startYearMonth.value, endYearMonth.value);

    // 単一フィールド項目と月別項目を分離
    const singleFieldItems = selectedOutputDataItems.value.filter((item) => isSingleFieldOutputKey(item.key));
    const monthlyItems = selectedOutputDataItems.value.filter((item) => !isSingleFieldOutputKey(item.key));

    // 月別出力データヘッダーを生成
    const monthlyHeaders: IPreviewHeader[] = [];

    if (outputFormat.value === OutputFormat.BY_MONTH) {
      // 月単位で横並び: 各月 → 全データタイプ
      yearMonths.forEach((yearMonth) => {
        monthlyItems.forEach((item) => {
          // 販売実績・入荷実績は未来月では表示しない
          if (isPastOnlyOutputKey(item.key) && isFutureMonth(yearMonth)) {
            return;
          }
          monthlyHeaders.push({
            id: `output-${item.key}-${yearMonth.year}-${yearMonth.month}-${index}`,
            label: `${yearMonth.year}年${yearMonth.month}月_${item.label}`,
            type: PreviewHeaderType.OUTPUT_DATA,
            outputDataKey: item.key,
            yearMonth: yearMonth,
            originalIndex: index++,
          });
        });
      });
    } else {
      // データ種類単位で横並び: 各データタイプ → 全月
      monthlyItems.forEach((item) => {
        yearMonths.forEach((yearMonth) => {
          // 販売実績・入荷実績は未来月では表示しない
          if (isPastOnlyOutputKey(item.key) && isFutureMonth(yearMonth)) {
            return;
          }
          monthlyHeaders.push({
            id: `output-${item.key}-${yearMonth.year}-${yearMonth.month}-${index}`,
            label: `${yearMonth.year}年${yearMonth.month}月_${item.label}`,
            type: PreviewHeaderType.OUTPUT_DATA,
            outputDataKey: item.key,
            yearMonth: yearMonth,
            originalIndex: index++,
          });
        });
      });
    }

    // 単一フィールド項目をinsertBeforeKeyに基づいて適切な位置に挿入
    singleFieldItems.forEach((singleItem) => {
      const singleHeader: IPreviewHeader = {
        id: `output-${singleItem.key}-${index}`,
        label: singleItem.label,
        type: PreviewHeaderType.OUTPUT_DATA,
        outputDataKey: singleItem.key,
        originalIndex: index++,
      };

      if (singleItem.insertBeforeKey) {
        // insertBeforeKeyに基づいて挿入位置を探す
        const insertIndex = monthlyHeaders.findIndex((h) => h.id === singleItem.insertBeforeKey);
        if (insertIndex !== -1) {
          monthlyHeaders.splice(insertIndex, 0, singleHeader);
        } else {
          // 見つからない場合は末尾に追加
          monthlyHeaders.push(singleHeader);
        }
      } else {
        // insertBeforeKeyがない場合は先頭（マスタ項目の直後）に追加
        monthlyHeaders.unshift(singleHeader);
      }
    });

    // 月別ヘッダーをすべて追加
    headers.push(...monthlyHeaders);

    return headers;
  });

  /**
   * プレビューの表示判定
   */
  const showPreview = computed(() => {
    return !!(startYearMonth.value && endYearMonth.value && outputFormat.value);
  });

  /**
   * 表示しきれない列数
   */
  const otherColumnsCount = computed(() => {
    const totalCount = previewHeaders.value.length;
    return totalCount > MAX_PREVIEW_HEADERS ? totalCount - MAX_PREVIEW_HEADERS : 0;
  });

  return {
    previewHeaders,
    showPreview,
    otherColumnsCount,
  };
};
